// Renders either a placeholder or the details of a recommended comp selection
import { isUndefined } from '@aloft/toolkit-shared';
import { LoadingIndicator } from '@aloft/toolkit-sui';

import { useState } from 'react';

import { useGetCompImages } from '../../../hooks/api/schema-hooks/useGetCompImages.ts';
import { DEFAULT_SELECTED_STATUSES } from '../../map/constants.ts';
import { PinStatusFilter } from '../../map/types.ts';
import { RecommendedCompSelectionDetailsProps } from '../types.ts';
import { EmptyCompSelectionDetails } from './details/EmptyCompSelectionDetails.tsx';
import { PropertyCompSelectionDetails } from './details/PropertyCompSelectionDetails.tsx';

const ZillowCompSelectionDetails = ({
  property,
  subject,
  data,
  comps,
  compSelectionTableRef,
  sorting,
  setFocusedRow,
  rowSelection,
  isInspectifyOrder,
  hasArvOption,
  compSelectionTableVariant = 'sales',
}: RecommendedCompSelectionDetailsProps) => {
  if (isUndefined(property)) return <EmptyCompSelectionDetails />;

  const [selectedStatuses, setSelectedStatuses] = useState<PinStatusFilter[]>(() => [
    ...DEFAULT_SELECTED_STATUSES,
  ]);

  const { id } = property;
  const getCompImages = useGetCompImages({ pathParams: { id } }, { retry: false });

  const isFetchingCachedImages = getCompImages.isLoading;
  if (isFetchingCachedImages) return <LoadingIndicator />;

  if (getCompImages.isSuccess && getCompImages.data.images.length > 0) {
    return (
      <PropertyCompSelectionDetails
        property={{ ...property, imageUrls: getCompImages.data.images }}
        key={property.id}
        subject={subject}
        data={data}
        comps={comps}
        compSelectionTableRef={compSelectionTableRef}
        sorting={sorting}
        setFocusedRow={setFocusedRow}
        rowSelection={rowSelection}
        isInspectifyOrder={isInspectifyOrder}
        hasArvOption={hasArvOption}
        selectedStatuses={selectedStatuses}
        onSelectedStatusesChange={setSelectedStatuses}
        compSelectionTableVariant={compSelectionTableVariant}
      />
    );
  }

  return (
    <PropertyCompSelectionDetails
      property={property}
      key={property.id}
      subject={subject}
      data={data}
      comps={comps}
      compSelectionTableRef={compSelectionTableRef}
      sorting={sorting}
      setFocusedRow={setFocusedRow}
      rowSelection={rowSelection}
      isInspectifyOrder={isInspectifyOrder}
      hasArvOption={hasArvOption}
      selectedStatuses={selectedStatuses}
      onSelectedStatusesChange={setSelectedStatuses}
      compSelectionTableVariant={compSelectionTableVariant}
    />
  );
};

export const CompSelectionDetails = ({
  orderID,
  property,
  subject,
  data,
  comps,
  compSelectionTableRef,
  sorting,
  setFocusedRow,
  rowSelection,
  isInspectifyOrder,
  hasArvOption,
  compSelectionTableVariant = 'sales',
}: RecommendedCompSelectionDetailsProps) => {
  if (isUndefined(property)) {
    return <EmptyCompSelectionDetails />;
  }

  const [selectedStatuses, setSelectedStatuses] = useState<PinStatusFilter[]>(() => [
    ...DEFAULT_SELECTED_STATUSES,
  ]);

  if (property.t === 'z') {
    return (
      <ZillowCompSelectionDetails
        orderID={orderID}
        property={property}
        subject={subject}
        data={data}
        comps={comps}
        sorting={sorting}
        compSelectionTableRef={compSelectionTableRef}
        setFocusedRow={setFocusedRow}
        rowSelection={rowSelection}
        isInspectifyOrder={isInspectifyOrder}
        hasArvOption={hasArvOption}
        compSelectionTableVariant={compSelectionTableVariant}
      />
    );
  }

  return (
    <PropertyCompSelectionDetails
      property={property}
      key={property.id}
      subject={subject}
      data={data}
      comps={comps}
      sorting={sorting}
      compSelectionTableRef={compSelectionTableRef}
      setFocusedRow={setFocusedRow}
      rowSelection={rowSelection}
      isInspectifyOrder={isInspectifyOrder}
      hasArvOption={hasArvOption}
      selectedStatuses={selectedStatuses}
      onSelectedStatusesChange={setSelectedStatuses}
      compSelectionTableVariant={compSelectionTableVariant}
    />
  );
};
